/**
 * FERRAMENTA CENTRAL DE BACKUP E RESTAURAÇÃO — BACKUP SERVICE
 * PROJETO: INSPECT IA
 * ESCOPO: ADMINISTRAÇÃO > BACKUP E RESTAURAÇÃO
 */

import type { RequestContext } from "../context/request-context.js";
import type { JsonObject } from "../../shared/kernel/json.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import type { AuthorizationService } from "../../permissions/authorization.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import {
  CURRENT_SCHEMA_VERSION,
  CURRENT_SYSTEM_VERSION,
  calculateSha256,
  type BackupManifest,
  type BackupPackage,
  type BackupScope,
  type BackupStorageMetrics,
  type BackupType,
  type CreateBackupParams,
  type RetentionPolicy,
  type RetentionResult
} from "../../domain/backup/backup-models.js";
import type { BackupRepository } from "../../repositories/backup-repository.js";

export interface BackupEntityCollector {
  readonly entityType: string;
  exportEntities(context: RequestContext, clientId?: string): Promise<readonly unknown[]>;
  restoreEntities?(context: RequestContext, entities: readonly unknown[]): Promise<number>;
}

export class BackupService {
  private entityCollectors = new Map<string, BackupEntityCollector>();

  constructor(
    private readonly backupRepo: BackupRepository,
    private readonly auth: AuthorizationService,
    private readonly audit?: AuditRecorder,
    collectors: readonly BackupEntityCollector[] = []
  ) {
    for (const collector of collectors) {
      this.entityCollectors.set(collector.entityType, collector);
    }
  }

  registerCollector(collector: BackupEntityCollector): void {
    this.entityCollectors.set(collector.entityType, collector);
  }

  getCollectors(): readonly BackupEntityCollector[] {
    return Array.from(this.entityCollectors.values());
  }

  async createBackup(
    context: RequestContext,
    params: CreateBackupParams = {}
  ): Promise<BackupPackage> {
    this.auth.require(context, PermissionCode.BackupCreate);

    const now = new Date();
    const timestamp = now.toISOString();
    const dateStr = now.toISOString().replace(/[-:T]/g, "").slice(0, 14);
    const randomSuffix = Math.random().toString(36).substring(2, 6).toUpperCase();
    const backupId = `BK-${dateStr}-${randomSuffix}`;

    const type: BackupType = params.type ?? "MANUAL";
    const scope: BackupScope = params.scope ?? "FULL";
    const clientId = params.customClientId ?? (context.activeClientId as string) ?? "GLOBAL";

    const databasePayload: Record<string, readonly unknown[]> = {};
    const entityCounts: Record<string, number> = {};

    if (scope === "FULL" || scope === "DATABASE") {
      for (const [entityType, collector] of this.entityCollectors.entries()) {
        try {
          const records = await collector.exportEntities(context, clientId !== "GLOBAL" ? clientId : undefined);
          databasePayload[entityType] = Array.isArray(records) ? JSON.parse(JSON.stringify(records)) : records;
          entityCounts[entityType] = records.length;
        } catch (err) {
          console.warn(`Failed to export entities for ${entityType}`, err);
          databasePayload[entityType] = [];
          entityCounts[entityType] = 0;
        }
      }
    }

    const payloadToHash = JSON.stringify({
      database: databasePayload,
      scope,
      clientId
    });

    const sha256Checksum = calculateSha256(payloadToHash);
    const sizeBytes = new TextEncoder().encode(payloadToHash).length;

    const manifest: BackupManifest = {
      id: backupId,
      createdAt: timestamp,
      createdBy: (context.userId as string) ?? "System",
      createdById: (context.userId as string) ?? "system",
      clientId,
      systemVersion: CURRENT_SYSTEM_VERSION,
      schemaVersion: CURRENT_SCHEMA_VERSION,
      type,
      scope,
      description: params.description ?? (type === "SAFETY_SNAPSHOT" ? "Snapshot de segurança pré-restauração" : "Backup manual"),
      sizeBytes,
      sha256Checksum,
      entityCounts,
      fileIntegrityMap: {},
      compatibilityTags: ["INSPECT_IA", `SCHEMA_${CURRENT_SCHEMA_VERSION}`]
    };

    const backupPackage: BackupPackage = {
      manifest,
      databasePayload,
      settingsPayload: {}
    };

    await this.backupRepo.saveBackupPackage(backupPackage);

    if (this.audit) {
      await this.audit.record(context, {
        action: type === "SAFETY_SNAPSHOT" ? AuditAction.BackupSafetySnapshotCreated : AuditAction.BackupCreated,
        entityType: "BackupPackage",
        entityId: backupId,
        previousState: null,
        newState: manifest as unknown as JsonObject,
        metadata: {
          type,
          scope,
          sizeBytes,
          entityCounts
        }
      });
    }

    // Auto cleanup if enabled
    const policy = await this.backupRepo.getRetentionPolicy();
    if (policy.autoCleanupEnabled) {
      try {
        await this.runRetentionCleanupInternal(context, policy);
      } catch (err) {
        console.warn("Auto cleanup retention failed", err);
      }
    }

    return backupPackage;
  }

  async listBackups(context: RequestContext): Promise<readonly BackupManifest[]> {
    this.auth.require(context, PermissionCode.BackupRead);
    const clientId = (context.activeClientId as string) ?? undefined;
    return this.backupRepo.listBackups(clientId);
  }

  async getBackup(context: RequestContext, id: string): Promise<BackupPackage | null> {
    this.auth.require(context, PermissionCode.BackupRead);
    return this.backupRepo.getBackupPackage(id);
  }

  async exportBackupJson(context: RequestContext, id: string): Promise<string> {
    this.auth.require(context, PermissionCode.BackupRead);
    const pkg = await this.backupRepo.getBackupPackage(id);
    if (!pkg) {
      throw new AppError(ErrorCode.NotFound, `Backup ${id} não encontrado.`, { safeMessage: "Backup não encontrado." });
    }
    return JSON.stringify(pkg, null, 2);
  }

  async deleteBackup(context: RequestContext, id: string): Promise<boolean> {
    this.auth.require(context, PermissionCode.BackupDelete);
    const pkg = await this.backupRepo.getBackupPackage(id);
    if (!pkg) {
      throw new AppError(ErrorCode.NotFound, `Backup ${id} não encontrado.`, { safeMessage: "Backup não encontrado." });
    }

    const deleted = await this.backupRepo.deleteBackup(id);

    if (deleted && this.audit) {
      await this.audit.record(context, {
        action: AuditAction.BackupDeleted,
        entityType: "BackupPackage",
        entityId: id,
        previousState: pkg.manifest as unknown as JsonObject,
        newState: null,
        metadata: { id }
      });
    }

    return deleted;
  }

  async getStorageMetrics(context: RequestContext): Promise<BackupStorageMetrics> {
    this.auth.require(context, PermissionCode.BackupRead);
    const manifests = await this.backupRepo.listBackups();

    let manualCount = 0;
    let automaticCount = 0;
    let safetySnapshotCount = 0;
    let totalSizeBytes = 0;
    let lastBackupDate: string | null = null;

    for (const m of manifests) {
      if (m.type === "MANUAL") manualCount++;
      else if (m.type === "AUTOMATIC") automaticCount++;
      else if (m.type === "SAFETY_SNAPSHOT") safetySnapshotCount++;

      totalSizeBytes += m.sizeBytes;
      if (!lastBackupDate || m.createdAt > lastBackupDate) {
        lastBackupDate = m.createdAt;
      }
    }

    let healthStatus: "HEALTHY" | "WARNING" | "ATTENTION_REQUIRED" = "HEALTHY";
    if (manifests.length === 0) {
      healthStatus = "ATTENTION_REQUIRED";
    } else if (lastBackupDate) {
      const daysSinceLastBackup = (Date.now() - new Date(lastBackupDate).getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceLastBackup > 7) {
        healthStatus = "WARNING";
      }
    }

    return {
      totalBackups: manifests.length,
      manualCount,
      automaticCount,
      safetySnapshotCount,
      totalSizeBytes,
      lastBackupDate,
      healthStatus
    };
  }

  async getRetentionPolicy(context: RequestContext): Promise<RetentionPolicy> {
    this.auth.require(context, PermissionCode.BackupRead);
    return this.backupRepo.getRetentionPolicy();
  }

  async updateRetentionPolicy(context: RequestContext, policy: RetentionPolicy): Promise<void> {
    this.auth.require(context, PermissionCode.BackupManage);
    await this.backupRepo.saveRetentionPolicy(policy);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.BackupSettingsUpdated,
        entityType: "BackupRetentionPolicy",
        entityId: "RETENTION_POLICY",
        previousState: null,
        newState: policy as unknown as JsonObject,
        metadata: { maxBackupsTotal: policy.maxBackupsTotal }
      });
    }
  }

  async runRetentionCleanup(context: RequestContext): Promise<RetentionResult> {
    this.auth.require(context, PermissionCode.BackupManage);
    const policy = await this.backupRepo.getRetentionPolicy();
    return this.runRetentionCleanupInternal(context, policy);
  }

  private async runRetentionCleanupInternal(
    context: RequestContext,
    policy: RetentionPolicy
  ): Promise<RetentionResult> {
    const manifests = await this.backupRepo.listBackups();
    const deletedBackupIds: string[] = [];
    let totalFreedBytes = 0;

    const safetySnapshots = manifests.filter(m => m.type === "SAFETY_SNAPSHOT");
    const regularBackups = manifests.filter(m => m.type !== "SAFETY_SNAPSHOT");

    // Enforce safety snapshots limit
    if (safetySnapshots.length > policy.maxSafetySnapshots) {
      const toDelete = safetySnapshots.slice(policy.maxSafetySnapshots);
      for (const m of toDelete) {
        await this.backupRepo.deleteBackup(m.id);
        deletedBackupIds.push(m.id);
        totalFreedBytes += m.sizeBytes;
      }
    }

    // Enforce regular backups limit
    if (regularBackups.length > policy.maxBackupsTotal) {
      const toDelete = regularBackups.slice(policy.maxBackupsTotal);
      for (const m of toDelete) {
        await this.backupRepo.deleteBackup(m.id);
        deletedBackupIds.push(m.id);
        totalFreedBytes += m.sizeBytes;
      }
    }

    return {
      deletedBackupIds,
      retainedCount: manifests.length - deletedBackupIds.length,
      totalFreedBytes
    };
  }
}
